import { memo, useMemo } from "react";

import {
    BRAND_COLORS,
    Column,
    FormatType,
    Icon,
    SeriesConfig,
    Text,
    TimeseriesChart,
    TooltipText,
    useAppPalette
} from "@bridgesplit/ui";
import {
    getUnixTs,
    JLP_MINT,
    ONYC_MINT,
    percentUiToDecimals,
    STAKED_SOL_MINT,
    TIME,
    USDC_MINT,
    WRAPPED_SOL_MINT
} from "@bridgesplit/utils";
import { usePrincipalCollateralBorrowRate, useTokenListContext } from "@bridgesplit/abf-react";

import { useMarketContext } from "./MarketContext";
import { useQuotesContext } from "./QuotesContext";

const COLLATERAL_COLORS = [
    BRAND_COLORS.BLUE,
    BRAND_COLORS.PRUSSIAN,
    BRAND_COLORS.GRAY,
    BRAND_COLORS.PINK,
    BRAND_COLORS.SKY,
    BRAND_COLORS.ORANGE,
    BRAND_COLORS.CREAM
];

const INITIAL_CHART_COLLATERALS = 5;
const MAX_CHART_COLLATERALS = 20;
const GLOBAL_KEY = "__global__";

export type RatesInfo = {
    date: number;
    [collateralMint: string]: number | undefined;
};

const now = getUnixTs();
const FALLBACK_DATA: RatesInfo[] = [
    {
        date: now - 3 * 86400,
        [USDC_MINT]: 0.025,
        [WRAPPED_SOL_MINT]: 0.035,
        [STAKED_SOL_MINT]: 0.029,
        [JLP_MINT]: 0.041,
        [ONYC_MINT]: 0.037
    },
    {
        date: now - 2 * 86400,
        [USDC_MINT]: 0.027,
        [WRAPPED_SOL_MINT]: 0.034,
        [STAKED_SOL_MINT]: 0.031,
        [JLP_MINT]: 0.039,
        [ONYC_MINT]: 0.036
    },
    {
        date: now - 1 * 86400,
        [USDC_MINT]: 0.028,
        [WRAPPED_SOL_MINT]: 0.033,
        [STAKED_SOL_MINT]: 0.03,
        [JLP_MINT]: 0.038,
        [ONYC_MINT]: 0.035
    },
    {
        date: now,
        [USDC_MINT]: 0.03,
        [WRAPPED_SOL_MINT]: 0.032,
        [STAKED_SOL_MINT]: 0.032,
        [JLP_MINT]: 0.04,
        [ONYC_MINT]: 0.038
    }
];

const COLLATERAL_FALLBACK_MINTS = [USDC_MINT, WRAPPED_SOL_MINT, STAKED_SOL_MINT, JLP_MINT, ONYC_MINT];

export default memo(function RatesComparison({
    height = 270,
    includeHeader = true
}: {
    height?: number;
    includeHeader?: boolean;
}) {
    const { getMetadata } = useTokenListContext();
    const { secondary } = useAppPalette();
    const { rateData, isLoading } = useRateHistory();

    const series: SeriesConfig<RatesInfo>[] = useMemo(() => {
        const collateralSeries: SeriesConfig<RatesInfo>[] = rateData.collateralMintsWithData.map((mint, i) => ({
            dataKey: mint as Exclude<keyof RatesInfo, "date"> & string,
            label: getMetadata(mint)?.symbol ?? mint,
            color: COLLATERAL_COLORS[i % COLLATERAL_COLORS.length]
        }));
        if (!rateData.hasGlobal) return collateralSeries;
        return [{ dataKey: GLOBAL_KEY, label: "All markets", color: secondary }, ...collateralSeries];
    }, [rateData.collateralMintsWithData, rateData.hasGlobal, getMetadata, secondary]);

    const headerComponents = useMemo(() => {
        return rateData.hasTooManySelectedCollateral ? <MaxSelectionWarning /> : undefined;
    }, [rateData.hasTooManySelectedCollateral]);

    const chart = (
        <TimeseriesChart
            chartId="rates-comparison"
            data={rateData.chartData}
            series={series}
            axisFormat={FormatType.Percent}
            height={height}
            loading={isLoading}
            hideLegend={!(series.length > 1)}
            headerComponents={headerComponents}
        />
    );

    if (includeHeader) {
        return (
            <Column width="100%" spacing={1}>
                <TooltipText helpText="Historical borrow APY by collateral on Loopscale" variant="h4">
                    Rate history
                </TooltipText>
                {chart}
            </Column>
        );
    }

    return (
        <Column width="100%" spacing={1}>
            {chart}
        </Column>
    );
});

function MaxSelectionWarning() {
    return (
        <TooltipText
            helpText={`A maximum of ${MAX_CHART_COLLATERALS} collateral assets can be viewed in this chart.`}
            icon={false}
        >
            <Text color="warning" variant="body2">
                <Icon type="warning" /> Limited view
            </Text>
        </TooltipText>
    );
}

function useRateHistory() {
    const { principalMint, loading: isRouteLoading } = useMarketContext();
    const { collateralMints: selectedCollateralMints } = useQuotesContext();
    const hasTooManySelectedCollateral = (selectedCollateralMints?.length ?? 0) > MAX_CHART_COLLATERALS;

    const {
        data,
        isFetching,
        isLoading: isBorrowRateLoading
    } = usePrincipalCollateralBorrowRate(
        {
            principalMint,
            timeLookback: TIME.MONTH,
            limit: selectedCollateralMints ? INITIAL_CHART_COLLATERALS : undefined,
            collateralMints: selectedCollateralMints ? selectedCollateralMints : undefined
        },
        { skip: !principalMint }
    );

    const isLoading = isRouteLoading || isBorrowRateLoading || isFetching;

    const rateData = useMemo(() => {
        if (!principalMint || !data || isLoading) {
            return {
                chartData: FALLBACK_DATA,
                collateralMints: COLLATERAL_FALLBACK_MINTS,
                collateralMintsWithData: COLLATERAL_FALLBACK_MINTS,
                hasGlobal: false,
                hasTooManySelectedCollateral
            };
        }

        const perCollateral = data.perCollateral ?? {};
        const globalRows = data.global ?? [];

        const allTimestamps = Array.from(
            new Set([
                ...Object.values(perCollateral).flatMap((records) =>
                    records.filter((r) => r.avgBorrowRate != null).map((r) => r.timestamp)
                ),
                ...globalRows.filter((r) => r.avgBorrowRate != null).map((r) => r.timestamp)
            ])
        ).sort((a, b) => a - b);

        const mintToRateMap: { [mint: string]: Map<number, number> } = {};
        for (const [mint, records] of Object.entries(perCollateral)) {
            mintToRateMap[mint] = new Map<number, number>();
            for (const record of records) {
                if (record.avgBorrowRate != null) {
                    mintToRateMap[mint].set(record.timestamp, percentUiToDecimals(record.avgBorrowRate));
                }
            }
        }

        const globalRateMap = new Map<number, number>();
        for (const record of globalRows) {
            if (record.avgBorrowRate != null) {
                globalRateMap.set(record.timestamp, percentUiToDecimals(record.avgBorrowRate));
            }
        }
        const hasGlobal = globalRateMap.size > 0;

        const collateralMintsWithData = Object.entries(mintToRateMap)
            .filter(([, rateMap]) => rateMap.size > 0)
            .map(([mint]) => mint);
        const collateralMints = selectedCollateralMints?.length ? selectedCollateralMints : collateralMintsWithData;
        const limitedCollateralMints = collateralMints.slice(0, MAX_CHART_COLLATERALS);
        const limitedCollateralMintsWithData = collateralMintsWithData
            .filter((mint) => limitedCollateralMints.includes(mint))
            .slice(0, MAX_CHART_COLLATERALS);

        if (!allTimestamps.length || (!limitedCollateralMintsWithData.length && !hasGlobal)) {
            return {
                chartData: [],
                collateralMints: limitedCollateralMints,
                collateralMintsWithData: [],
                hasGlobal,
                hasTooManySelectedCollateral
            };
        }

        const interpolatedRateMap: { [mint: string]: Map<number, number> } = {};
        for (const mint of limitedCollateralMintsWithData) {
            interpolatedRateMap[mint] = interpolateMissingRates(mintToRateMap[mint], allTimestamps);
        }
        const interpolatedGlobal = hasGlobal ? interpolateMissingRates(globalRateMap, allTimestamps) : undefined;

        const chartData = allTimestamps
            .map((timestamp) => {
                const entry: RatesInfo = { date: timestamp };
                for (const mint of limitedCollateralMintsWithData) {
                    const interpolated = interpolatedRateMap[mint].get(timestamp);
                    if (interpolated !== undefined) {
                        entry[mint] = interpolated;
                    }
                }
                if (interpolatedGlobal) {
                    const g = interpolatedGlobal.get(timestamp);
                    if (g !== undefined) entry[GLOBAL_KEY] = g;
                }
                return entry;
            })
            .filter(
                (entry) =>
                    limitedCollateralMintsWithData.some((mint) => entry[mint] !== undefined) ||
                    entry[GLOBAL_KEY] !== undefined
            )
            .sort((a, b) => a.date - b.date);

        return {
            chartData,
            collateralMints: limitedCollateralMints,
            collateralMintsWithData: limitedCollateralMintsWithData,
            hasGlobal,
            hasTooManySelectedCollateral
        };
    }, [data, hasTooManySelectedCollateral, principalMint, selectedCollateralMints, isLoading]);

    return { rateData, isLoading, isFetching };
}

function interpolateMissingRates(rateMap: Map<number, number>, sortedTimestamps: number[]): Map<number, number> {
    const result = new Map<number, number>();
    let prevIdx = -1;
    let prevVal: number | undefined = undefined;

    for (let i = 0; i < sortedTimestamps.length; i++) {
        const ts = sortedTimestamps[i];
        const v = rateMap.get(ts);

        if (v !== undefined) {
            result.set(ts, v);
            prevIdx = i;
            prevVal = v;
        } else {
            let nextIdx = -1;
            let nextVal: number | undefined = undefined;
            for (let j = i + 1; j < sortedTimestamps.length; j++) {
                const ts2 = sortedTimestamps[j];
                const v2 = rateMap.get(ts2);
                if (v2 !== undefined) {
                    nextIdx = j;
                    nextVal = v2;
                    break;
                }
            }
            if (prevVal !== undefined && nextVal !== undefined && prevIdx !== -1 && nextIdx !== -1) {
                const t = (i - prevIdx) / (nextIdx - prevIdx);
                const interpVal = prevVal + (nextVal - prevVal) * t;
                result.set(ts, interpVal);
            } else if (prevVal !== undefined) {
                result.set(ts, prevVal);
            } else if (nextVal !== undefined) {
                result.set(ts, nextVal);
            }
        }
    }
    return result;
}
